import type { ServiceRequestStatus } from '../types/serviceRequestListType';

/**
 * Statuses from which an SR can be rescheduled. Mirrors the BE
 * `ReschedulePolicyService.RESCHEDULABLE_FROM` allow-list.
 *
 * `unfulfilled` is reschedulable (RR-622): once a technician reports a missed
 * visit, the admin can put the SR back on the calendar. Like `delayed` it is
 * waived from the 24h lead-time rule (see {@link GATED_24H}).
 */
export const RESCHEDULABLE_FROM: readonly ServiceRequestStatus[] = [
  'assigned',
  'rejected',
  'delayed',
  'unfulfilled',
];

/** Statuses subject to the 24h lead-time rule (waived for delayed/unfulfilled/rejected). */
const GATED_24H: readonly ServiceRequestStatus[] = ['assigned'];

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export type RescheduleBlockReason = 'STATUS' | 'WITHIN_24H';

export interface RescheduleEligibility {
  canReschedule: boolean;
  reason?: RescheduleBlockReason;
}

/**
 * FE mirror of the BE 24h-lead-time + status allow-list gate.
 *
 * - Returns `STATUS` when the current status is not reschedulable at all.
 * - Returns `WITHIN_24H` only when status is gated AND `scheduledStartAt`
 *   is non-null AND the visit is < 24h away. Null `scheduled_start_at` is
 *   treated as "no prior visit to protect" (matches the BE belt-and-braces
 *   guard for REJECTED rows whose schedule was nulled).
 *
 * Re-compute on every render — do not cache. BE is the final gate.
 */
export const isReschedulableNow = (
  status: ServiceRequestStatus | undefined,
  scheduledStartAt: string | null | undefined,
  now: number = Date.now()
): RescheduleEligibility => {
  if (!status || !RESCHEDULABLE_FROM.includes(status)) {
    return { canReschedule: false, reason: 'STATUS' };
  }
  if (!GATED_24H.includes(status)) {
    return { canReschedule: true };
  }
  if (!scheduledStartAt) {
    return { canReschedule: true };
  }
  const startMs = Date.parse(scheduledStartAt);
  if (Number.isNaN(startMs)) {
    return { canReschedule: true };
  }
  if (startMs - now < TWENTY_FOUR_HOURS_MS) {
    return { canReschedule: false, reason: 'WITHIN_24H' };
  }
  return { canReschedule: true };
};
